import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import {
  Briefcase, HeartStraight, ChartLineUp, ArrowRight,
  CheckCircle, MapPin, Sparkle, Star,
} from "@phosphor-icons/react";
import CTABand from "@/components/site/CTABand";
import SectionHeading from "@/components/site/SectionHeading";
import { LogoImage } from "@/components/Logo";
import { api } from "@/lib/api";
import { useContent } from "@/context/ContentContext";
import { DEFAULTS } from "@/data/contentRegistry";
import Marquee from "react-fast-marquee";

const ICONS = [Briefcase, HeartStraight, ChartLineUp];
const SERVICE_LINKS = [
  "/assurances-professionnelles",
  "/sante-prevoyance",
  "/patrimoine-placements",
];

function readContent(content, key) {
  const v = content?.[key];
  if (v !== undefined && v !== null && v !== "") return v;
  return DEFAULTS[key] ?? "";
}
function readImage(content, key) {
  const raw = readContent(content, key);
  if (!raw) return "";
  if (raw.startsWith("/api/uploads/")) {
    return `${process.env.REACT_APP_BACKEND_URL || ""}${raw}`;
  }
  return raw;
}

export default function Home() {
  const { content } = useContent();
  const tx = (k) => readContent(content, k);
  const im = (k) => readImage(content, k);

  const [partners, setPartners] = useState([]);
  useEffect(() => { api.get("/partners").then(({ data }) => setPartners(data || [])); }, []);

  const heroImg = im("home.hero.image");
  const cabinetImg = im("home.cabinet.image");

  return (
    <div data-testid="page-home">
      {/* HERO */}
      <section className="relative min-h-[calc(100vh-7rem)] flex items-center overflow-hidden">
        <div className="absolute inset-0">
          {heroImg && <img src={heroImg} alt="" className="w-full h-full object-cover opacity-40" />}
          <div className="absolute inset-0 bg-gradient-to-r from-ducs-night via-ducs-night/85 to-ducs-night/30" />
          <div className="absolute inset-0 dijon-pattern opacity-30" />
        </div>
        <div className="relative max-w-7xl mx-auto px-5 md:px-8 py-24 md:py-28 grid lg:grid-cols-12 gap-12 items-center w-full">
          <div className="lg:col-span-7">
            <div className="flex items-center gap-2 label-caps mb-6">
              <MapPin size={14} className="text-ducs-gold" /> {tx("home.hero.eyebrow")}
            </div>
            <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl text-ducs-cream leading-[1.05] tracking-tight">
              {tx("home.hero.title.line1")}<br /> {tx("home.hero.title.line2")}<br />
              <span className="text-ducs-gold italic">{tx("home.hero.title.accent")}</span>
            </h1>
            <div className="gold-divider mt-8" />
            <p className="mt-8 text-ducs-mist text-lg leading-relaxed max-w-2xl whitespace-pre-line">
              {tx("home.hero.lead")}
            </p>
            <div className="mt-10 flex flex-col sm:flex-row gap-4">
              <Link to="/contact" data-testid="hero-cta-audit" className="btn-primary">
                {tx("home.hero.cta1")} <ArrowRight size={16} weight="bold" />
              </Link>
              <Link to="/methode" data-testid="hero-cta-solutions" className="btn-secondary">
                {tx("home.hero.cta2")}
              </Link>
            </div>

            <div className="mt-14 grid grid-cols-2 sm:grid-cols-3 gap-6 max-w-2xl">
              {[1, 2, 3].map((i) => (
                <div key={i} className="border-l border-ducs-gold/40 pl-4">
                  <div className="text-ducs-gold font-serif text-xl">{tx(`home.pillar${i}.title`)}</div>
                  <div className="text-xs text-ducs-mist mt-1 leading-snug">{tx(`home.pillar${i}.body`)}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Large decorative logo on the right (desktop only) */}
          <div className="hidden lg:flex lg:col-span-5 items-center justify-center">
            <div className="relative">
              <div className="absolute inset-0 bg-ducs-gold/5 blur-3xl rounded-full" />
              <LogoImage className="relative max-h-[26rem] w-auto drop-shadow-[0_0_40px_rgba(212,167,68,0.15)]" />
            </div>
          </div>
        </div>
      </section>

      {/* PROMESSE */}
      <section className="relative py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-5 md:px-8 grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-5 relative">
            <div className="aspect-[4/5] overflow-hidden rounded-sm border border-ducs-gold/20">
              {cabinetImg && <img src={cabinetImg} alt={tx("home.cabinet.signature.name")} className="w-full h-full object-cover object-top" />}
            </div>
            <div className="absolute -bottom-6 -right-6 bg-ducs-navy border border-ducs-gold/30 p-6 max-w-xs hidden sm:block">
              <Sparkle size={22} className="text-ducs-gold" />
              <div className="mt-3 font-serif text-xl text-ducs-cream">{tx("home.cabinet.signature.name")}</div>
              <div className="text-xs text-ducs-mist mt-1">{tx("home.cabinet.signature.role")}</div>
            </div>
          </div>
          <div className="lg:col-span-7">
            <SectionHeading
              eyebrow={tx("home.cabinet.eyebrow")}
              title={<>{tx("home.cabinet.title.line1")}<br />{tx("home.cabinet.title.line2")}</>}
              subtitle={tx("home.cabinet.body")}
            />
            <div className="mt-10 grid gap-4">
              {[1, 2, 3, 4].map((i) => (
                <div key={i} className="flex items-start gap-3">
                  <CheckCircle size={20} weight="duotone" className="text-ducs-gold mt-0.5 shrink-0" />
                  <span className="text-ducs-cream/90">{tx(`home.cabinet.bullet${i}`)}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CHIFFRES CLÉS */}
      <section className="relative py-20 md:py-28 bg-ducs-navy border-y border-ducs-gold/15" data-testid="stats-section">
        <div className="absolute inset-0 dijon-pattern opacity-25" aria-hidden />
        <div className="relative max-w-7xl mx-auto px-5 md:px-8">
          <SectionHeading
            eyebrow={tx("home.stats.eyebrow")}
            title={tx("home.stats.title")}
            subtitle={tx("home.stats.subtitle")}
            align="center"
          />
          <div className="mt-14 grid md:grid-cols-3 gap-10 md:gap-6">
            {[1, 2, 3].map((i) => (
              <StatCard
                key={i}
                index={i}
                value={tx(`home.stat${i}.value`)}
                label={tx(`home.stat${i}.label`)}
                description={tx(`home.stat${i}.description`)}
              />
            ))}
          </div>
        </div>
      </section>

      {/* SERVICES (3 cartes) */}
      <section className="relative py-24 md:py-32 bg-ducs-navy/40">
        <div className="max-w-7xl mx-auto px-5 md:px-8">
          <SectionHeading eyebrow={tx("home.services.eyebrow")} title={tx("home.services.title")} />
          <div className="mt-14 grid md:grid-cols-3 gap-6">
            {[1, 2, 3].map((i) => {
              const Icon = ICONS[i - 1];
              return (
                <Link to={SERVICE_LINKS[i - 1]} key={i} data-testid={`service-card-${i - 1}`} className="group card-ducs p-8 md:p-10 flex flex-col">
                  <Icon size={36} weight="duotone" className="text-ducs-gold" />
                  <h3 className="mt-6 font-serif text-2xl md:text-3xl text-ducs-cream">{tx(`home.service${i}.title`)}</h3>
                  <p className="mt-4 text-ducs-mist leading-relaxed">{tx(`home.service${i}.body`)}</p>
                  <div className="mt-auto pt-6 flex items-center gap-2 text-ducs-gold text-sm tracking-wide">
                    Découvrir <ArrowRight size={14} className="transition-transform group-hover:translate-x-1" />
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* METHODE PREVIEW */}
      <section className="relative py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-5 md:px-8">
          <SectionHeading eyebrow={tx("home.methode.eyebrow")} title={tx("home.methode.title")} subtitle={tx("home.methode.subtitle")} />
          <div className="mt-14 grid md:grid-cols-2 lg:grid-cols-5 gap-4">
            {[1, 2, 3, 4, 5].map((i) => (
              <div key={i} className="border border-ducs-gold/20 p-6 hover:border-ducs-gold/60 transition-colors">
                <div className="font-serif text-3xl text-ducs-gold">{tx(`methode.step${i}.number`)}</div>
                <div className="gold-divider mt-3" />
                <div className="mt-4 font-serif text-lg text-ducs-cream">{tx(`methode.step${i}.title`)}</div>
                <p className="mt-3 text-sm text-ducs-mist leading-relaxed">{tx(`methode.step${i}.body`)}</p>
              </div>
            ))}
          </div>
          <div className="mt-12">
            <Link to="/methode" className="btn-secondary" data-testid="methode-cta">{tx("home.methode.button")}</Link>
          </div>
        </div>
      </section>

      {/* TÉMOIGNAGES */}
      <section className="relative py-24 md:py-32 bg-ducs-navy/40 border-y border-ducs-gold/10" data-testid="testimonials-section">
        <div className="absolute inset-0 dijon-pattern opacity-20" aria-hidden />
        <div className="relative max-w-7xl mx-auto px-5 md:px-8">
          <SectionHeading
            eyebrow={tx("home.testimonials.eyebrow")}
            title={tx("home.testimonials.title")}
            subtitle={tx("home.testimonials.subtitle")}
            align="center"
          />
          <div className="mt-14 grid md:grid-cols-3 gap-6">
            {[1, 2, 3].map((i) => (
              <TestimonialCard
                key={i}
                index={i}
                name={tx(`home.testimonial${i}.name`)}
                role={tx(`home.testimonial${i}.role`)}
                quote={tx(`home.testimonial${i}.quote`)}
                rating={parseInt(tx(`home.testimonial${i}.rating`), 10) || 5}
              />
            ))}
          </div>
        </div>
      </section>

      {/* PARTENAIRES MARQUEE */}
      <section className="relative py-20 bg-ducs-night border-b border-ducs-gold/10">
        <div className="max-w-7xl mx-auto px-5 md:px-8 mb-10">
          <div className="label-caps mb-4">{tx("home.partners.eyebrow")}</div>
          <h2 className="font-serif text-2xl md:text-3xl text-ducs-cream max-w-3xl">{tx("home.partners.title")}</h2>
        </div>
        {partners.length > 0 && (
          <Marquee gradient gradientColor="#131b2c" gradientWidth={120} speed={28} pauseOnHover>
            {partners.map((p) => (
              <div key={p.id} className="partner-tile">
                {p.logo_url
                  ? <img src={p.logo_url} alt={p.name} className="max-h-12 max-w-[140px] object-contain" />
                  : <span>{p.name}</span>}
              </div>
            ))}
          </Marquee>
        )}
        <div className="max-w-7xl mx-auto px-5 md:px-8 mt-10">
          <Link to="/partenaires" className="btn-secondary" data-testid="partners-cta">{tx("home.partners.button")}</Link>
        </div>
      </section>

      <CTABand
        eyebrow={tx("home.cta.eyebrow")}
        title={tx("home.cta.title")}
        subtitle={tx("home.cta.subtitle")}
        primaryLabel={tx("home.cta.button1")}
        secondaryLabel={tx("home.cta.button2")}
      />
    </div>
  );
}

function StatCard({ index, value, label, description }) {
  return (
    <div
      data-testid={`stat-${index}`}
      className="text-center md:text-left md:border-l md:border-ducs-gold/30 md:pl-8"
    >
      <div className="font-serif text-5xl md:text-6xl lg:text-7xl text-ducs-gold leading-none tracking-tight">
        {value}
      </div>
      <div className="label-caps mt-4 text-ducs-cream">{label}</div>
      {description && (
        <p className="text-sm text-ducs-mist mt-3 leading-relaxed max-w-xs mx-auto md:mx-0">
          {description}
        </p>
      )}
    </div>
  );
}

function TestimonialCard({ index, name, role, quote, rating }) {
  const clamped = Math.max(0, Math.min(5, rating));
  return (
    <article
      data-testid={`testimonial-${index}`}
      className="card-ducs hover:translate-y-0 p-7 md:p-8 flex flex-col h-full"
    >
      <div className="flex items-center gap-1 mb-5">
        {Array.from({ length: 5 }).map((_, i) => (
          <Star
            key={i}
            size={16}
            weight={i < clamped ? "fill" : "regular"}
            className={i < clamped ? "text-ducs-gold" : "text-ducs-gold/25"}
          />
        ))}
      </div>
      <blockquote className="font-serif text-lg md:text-xl text-ducs-cream leading-relaxed italic flex-1">
        <span className="text-ducs-gold mr-1">«</span>
        {quote}
        <span className="text-ducs-gold ml-1">»</span>
      </blockquote>
      <div className="mt-6 pt-5 border-t border-ducs-gold/15">
        <div className="font-serif text-ducs-gold text-base">{name}</div>
        <div className="text-xs text-ducs-mist tracking-wide mt-0.5">{role}</div>
      </div>
    </article>
  );
}
